/**
 * Estilos PADRÃO do react-select por token (PO1-2724), extraídos de
 * Indicadores/Faturamento/FaturamentoFiltros.js. Acompanham tema claro/escuro
 * e a cor de destaque sem bloco `white-content`.
 *
 * Uso:
 *   <Select classNamePrefix="react-select" styles={reactSelectStyles} {...reactSelectPortalProps()} />
 *
 * Dentro de `card-everlog-shell` (overflow: hidden) o portal é obrigatório,
 * senão o menu é cortado pelo card.
 */
export function makeReactSelectStyles(options) {
  const opts = options || {};
  const menuZIndex = opts.menuZIndex === undefined ? 30 : opts.menuZIndex;
  const portalZIndex = opts.portalZIndex === undefined ? 50 : opts.portalZIndex;
  const minHeight = opts.minHeight === undefined ? 42 : opts.minHeight;
  const radius = opts.radius === undefined ? 14 : opts.radius;

  return {
    menu: (base) => ({
      ...base,
      zIndex: menuZIndex,
      background: "var(--everlog-card-surface-flat)",
      border: "1px solid var(--everlog-card-border)",
      borderRadius: radius,
      boxShadow: "var(--everlog-card-shadow)",
    }),
    menuPortal: (base) => ({ ...base, zIndex: portalZIndex }),
    control: (base, state) => ({
      ...base,
      background: "var(--everlog-card-surface-field)",
      backgroundColor: "var(--everlog-card-surface-field)",
      borderColor: state.isFocused ? "var(--theme-accent)" : "var(--everlog-card-border)",
      borderRadius: radius,
      minHeight,
      opacity: state.isDisabled ? 0.78 : 1,
      boxShadow: state.isFocused ? "0 0 0 3px var(--theme-accent-glow)" : "none",
      transition: "border-color 0.18s ease, box-shadow 0.18s ease",
    }),
    valueContainer: (base) => ({ ...base, padding: "2px 10px" }),
    multiValue: (base) => ({
      ...base,
      background: "var(--everlog-card-surface-pill)",
      border: "1px solid var(--everlog-card-border-faint)",
      borderRadius: 999,
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "var(--everlog-card-title)",
      fontWeight: 600,
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "var(--everlog-card-muted)",
      borderRadius: 999,
      ":hover": {
        background: "var(--everlog-card-surface-active)",
        color: "var(--everlog-card-title)",
      },
    }),
    singleValue: (base) => ({
      ...base,
      color: "var(--everlog-card-title)",
    }),
    input: (base) => ({
      ...base,
      color: "var(--everlog-card-text)",
    }),
    placeholder: (base) => ({
      ...base,
      color: "var(--everlog-card-muted)",
    }),
    option: (base, state) => ({
      ...base,
      background: state.isFocused
        ? "var(--everlog-card-interactive-bg)"
        : state.isSelected
        ? "var(--everlog-card-surface-active)"
        : "var(--everlog-card-surface-flat)",
      color: "var(--everlog-card-text)",
      cursor: "pointer",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "var(--everlog-card-muted)",
    }),
    clearIndicator: (base) => ({
      ...base,
      color: "var(--everlog-card-muted)",
    }),
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: "var(--everlog-card-border)",
    }),
  };
}

export const reactSelectStyles = makeReactSelectStyles();

/**
 * Menu em portal é `position: fixed`: se o container do select rolar (ex.: corpo
 * de um Modal), o menu fica parado na tela enquanto o campo sai do lugar. Fecha
 * o menu quando rola um ancestral do campo focado; a rolagem da própria lista de
 * opções (que não contém o campo) não fecha.
 */
export function fecharMenuAoRolarContainer(event) {
  const alvo = event && event.target;
  if (!alvo || typeof document === "undefined") return false;
  if (alvo === document || alvo === document.documentElement || alvo === document.body) return true;
  const focado = document.activeElement;
  return !!(focado && typeof alvo.contains === "function" && alvo.contains(focado));
}

/** Props de portal para o menu não ser cortado por `overflow: hidden` dos cards. */
export function reactSelectPortalProps() {
  if (typeof document === "undefined") return {};
  return {
    menuPortalTarget: document.body,
    menuPosition: "fixed",
    closeMenuOnScroll: fecharMenuAoRolarContainer,
  };
}

export default reactSelectStyles;
